import { API_STATUS } from 'core-lib/constants/network';
import utilityVariables from 'ui-v2/styles/utility_colors.module.scss';
import {
  primitivesColors,
  semanticColors,
  utilityColors,
} from 'ui-v2/tokens/colors';

export enum ReviewType {
  carReview = 'vehicle_review',
  dealerReview = 'dealer_review',
}

export enum DealerMappingType {
  DEALER_NOT_EXIST = 'DEALER_NOT_EXIST',
  DEALER_MAPPED = 'DEALER_MAPPED',
  DEALER_ADDED_BY_USER = 'DEALER_ADDED_BY_USER',
}

export const formatNumberWithCommas = (num: number | string): string => {
  if (!num) return '0';
  return Number(num).toLocaleString('en-IN');
};

export const colorArray = [
  {
    bgColor: 'linear-gradient(160deg, #fff -21.24%, #ffecb3 131.3%)',
    color: 'linear-gradient(134deg, #ffb201 0%, #854d0e 100%)',
  },
  {
    bgColor: 'linear-gradient(161deg,  #FFF -20.88%,  #FCDFE8 134.9%)',
    color: 'linear-gradient(135deg,  #F7306F 0%, #800026 100%)',
  },
  {
    bgColor: 'linear-gradient(160deg,  #FFF 0%,  #9EE9E4 114.56%)',
    color: 'linear-gradient(134deg,  #2F8E88 0%,  #033D38 100%)',
  },
  {
    bgColor: 'linear-gradient(160deg,  #FFF -8.56%,  #BBDAFF 108.9%)',
    color: 'linear-gradient(134deg,  #2A8DFF 0%,  #002888 100%)',
  },
];

export const additionalFacts = [
  'Your reviews don’t just share your experience—they help others make informed decisions and connect you with a large community of car owners',
  'Park+ is India’s most trusted and largest community of car lovers, bringing car owners and buyers together',
  'Park+ is the only platform in India that verifies users before allowing them to submit a review, ensuring trust and honesty',
  '80% of car dealers track their reviews on Park+, making your feedback more impactful',
  'Park+ has the highest number of car and dealer reviews among all online platforms, giving you the most reliable insights',
];

export const carFacts = [
  'At Park+, over 2 lakh users visit car and dealer pages daily—your review can help them make informed decisions!',
  'India has 5 crore car owners, and 2 crore of them are on Park+. Be part of India’s largest car community!',
  'Users who read at least five car reviews on Park+ feel twice as confident in their purchase decisions',
  'At Park+, you can see your friends’ reviews and learn about their car ownership experiences',
  'The Park+ community is 2 Crore+ strong, bringing together passionate car lovers like you!',
  'Did you know? The most popular car on Park+ is the Maruti Baleno based on user ratings!',
  'Over 75% of car buyers at Park+ check at least 3 reviews before making their final choice',
  '1 out of every 3 car reviews online is listed on Park+—making us the go-to platform for car buyers!',
];

export const dealerFacts = [
  '85% of customers say dealer reviews on Park+ helped them choose the right showroom',
  'Transparency matters! Every dealer rating is verified for authenticity at Park+',
  'The top-rated dealer on Park+ has helped over 1 lakh customers find their dream car',
  'Great customer service makes the difference—85% of dealer reviews focus on salesperson behavior',
  'Across all online platforms, Park+ has the highest number of car and dealer reviews!',
  'The most popular dealer on Park+ is Zedex Tata—have you checked them out?',
  'For a hassle-free car buying experience, choose a Park+ Authorized Dealer—verified for transparent pricing, seamless paperwork, and great service!',
];

export const IconUrls = [
  {
    icon: 'https://parkplus-bkt-img.parkplus.io/production/team/public/FS_20250106154928470356_d46328aa-6c16-44ca-8c6e-9b82cb164c59.webp',
    title: 'I want to explore on my own',
    redirection: '',
    border: '1px solid #a3d1ff',
    bgColor: primitivesColors.blue[50],
    selectedBgColor:
      'linear-gradient(137deg, rgba(254,159,189,1) 5%, rgba(224,24,84,1) 91%)',
    selectedColor: utilityVariables?.[`utility_icon_white_alpha_tertiary`],
    cardName: 'explore',
  },
  {
    icon: 'https://parkplus-bkt-img.parkplus.io/production/pnd/public/icon_20240806181502544129.png',
    title: 'Check out your friends’ cars',
    redirection: '',
    border: '1px solid #FE9FBD',
    bgColor: utilityVariables?.[`utility_text_rosewood_primary`],
    selectedBgColor:
      'linear-gradient(137deg, rgba(254,159,189,1) 5%, rgba(224,24,84,1) 91%)',
    selectedColor: utilityVariables?.[`utility_icon_white_alpha_tertiary`],
    cardName: 'friends_car',
  },
  {
    icon: 'https://parkplus-bkt-img.parkplus.io/production/pnd/public/icon_20240806181328844143.png',
    title: 'Let us help you find your dream car',
    redirection: '',
    border: '1px solid #6CCB70',
    bgColor: utilityVariables?.[`utility_text_green_primary`],
    selectedBgColor:
      'linear-gradient(137deg, rgba(108,203,112,1) 5%, rgba(0,130,0,1) 91%)',
    selectedColor: utilityVariables?.[`utility_icon_white_alpha_tertiary`],
    cardName: 'bot_assisted',
  },
  // {
  //   icon: 'https://parkplus-bkt-img.parkplus.io/production/pnd/public/icon_20240805182939773365.png',
  //   title: 'I want to explore on my own',
  //   redirection: '',
  // },
];

export enum FilterWidgets {
  Budget = 'price',
  BodyType = 'body',
  Fuel = 'fuel',
  Mileage = 'mileage',
  Brands = 'brand_id',
  SeatingCapacity = 'seating_capacity',
  FavFeatures = 'tags',
}

export const NewCarsPdpFaq = [
  {
    question:
      'What are the key features I should look for when buying a new car?',
    answer:
      'Focus on fuel efficiency, safety features (like airbags, ABS), and tech like infotainment or driver assistance systems. Comfort, budget, and resale value are also important considerations.',
  },
  {
    question:
      'How does fuel efficiency vary between different types of cars (e.g., petrol, diesel, hybrid, electric)?',
    answer:
      'Electric and hybrid cars typically offer the best fuel efficiency, followed by diesel, then petrol cars. However, efficiency also depends on driving conditions and habits.',
  },
  {
    question: 'What maintenance is required to keep a car in good condition?',
    answer:
      "Regular oil changes, tire checks, and servicing of brakes and filters are essential. Keep up with the manufacturer's maintenance schedule for optimal performance.",
  },
  {
    question: 'What safety features are most important in modern cars?',
    answer:
      'Key safety features include airbags, anti-lock braking systems (ABS), traction control, and electronic stability control (ESC). Advanced driver assistance systems (ADAS) like lane assist and automatic braking are also valuable.',
  },
  {
    question:
      'How do different car models compare in terms of performance and reliability?',
    answer:
      "Performance varies based on engine power, transmission, and handling, while reliability depends on the brand's build quality and user reviews. Research specific models for detailed comparisons.",
  },
];

export const NEW_CARS_ROUTES = {
  carCompare: '/new-cars-v2/compare-cars',
  confirmation: '/new-cars-v2/confirmation',
  search: '/new-cars-v2/search',
  searchResults: '/new-cars-v2/search/results',
  landingPage: '/new-cars-v2',
  reviews: '/new-cars-v2/car-reviews/',
  testDriveBookings: '/new-cars-v2/my-bookings',
  dealerMarketing: '/new-cars-v2/dealer-marketing/home',
  dealerDetail: '/new-cars-v2/dealer-marketing/dealer/',
  dealerSearch: '/new-cars-v2/dealer-marketing/dealers-nearby',
  findDreamCar: '/new-cars-v2/find-dream-car',
};

export enum FiltersMapping {
  FuelType = 'fuel',
  TransmissionType = 'transmission',
}

export const FilterTypes = {
  [FiltersMapping.FuelType]: 'fuelType',
  [FiltersMapping.TransmissionType]: 'transmissionType',
};

export const STICKY_ICON =
  'https://parkplus-bkt-img.parkplus.io/production/fuel-bill/public/Banner013_20230803235712876166.png';

export const CAR_ERROR_IMAGE =
  'https://parkplus-bkt-img.parkplus.io/production/vehicle/public/car_20230806194756561303.png';

export enum ID_MAPPING {
  LandingPage = 'landingRoot',
  SearchResults = 'searchResultsRoot',
  ModelDetails = 'modelDetailsRoot',
  SearchResultsFromSearch = 'searchResultsFromSearch',
}

export const badWords = [
  'yes',
  'no',
  'not',
  'available',
  'and',
  'in',
  'the',
  'on',
  'with',
];

export const INPUT_ERR =
  'A review must consist of at least 60 characters to be impactful';

//status [2] indicates approved reviews
export const ratingFilters = [
  {
    title: 'Most Recent',
    payload: {
      status: [2],
    },
  },
  {
    title: 'Positive first',
    payload: {
      status: [2],
      rating: {
        gte: 3,
      },
    },
  },
  {
    title: 'Negative first',
    payload: {
      status: [2],
      rating: {
        lte: 2,
      },
    },
  },
];

export const RATING_LIST_LIMIT = 4;

export const EMI_INTEREST = 8;

export const reviewInitState = {
  overallRating: null,
  tabs: {
    0: {
      list: [],
      apiStatus: API_STATUS.default,
      page: 1,
      hasMore: true,
    },
    1: {
      list: [],
      apiStatus: API_STATUS.default,
      page: 1,
      hasMore: true,
    },
    2: { list: [], apiStatus: API_STATUS.default, page: 1, hasMore: true },
  },
  activeTab: 0,
  modelId: null,
};

export enum threeSixtyKeys {
  Interior = '360Exterior',
  Exterior = '360Interior',
}

export const CAR_IMAGE_FALLBACK =
  'https://parkplus-bkt-img.parkplus.io/production/fuel-bill/public/Banner013_20230727191745795308.png';

export enum ScoreKeys {
  Image = 'image',
  Colors = 'colors',
  View = 'view',
  EMI = 'emi',
  Specifications = 'specifications',
  Variants = 'variants',
  Compare = 'compare',
  SimilarCarCompare = 'similar_car_compare',
  Review = 'review',
  ThreeSixty = '360',
  Price = 'price',
  ViewAllFeatures = 'view_all_features',
  ContactStrip = 'contact_strip',
  City = 'city',
}

export const BookingTabs = {
  0: 'BOOKING_CONFIRMED',
  1: 'TEST_DRIVE_TAKEN',
  2: 'BILL_UPLOADED',
};

export const TABS = ['Booking Confirmed', 'Test drive Taken', 'Bill uploaded'];

export const HOW_IT_WORKS = [
  {
    icon: 'https://parkplus-bkt-img.parkplus.io/production/vehicle/public/cards_20230828110136792665.png',
    title: ' worth of free IndianOil Fuel',
    subtitle: 'Enjoy free fuel for next 6 months, upto 10 litres per month',
    amount: '₹6000',
  },
  {
    icon: 'https://parkplus-bkt-img.parkplus.io/production/vehicle/public/parking_20230828110204027601.png',
    title: ' worth of free car parking',
    subtitle:
      'Enjoy a year of complementary car parking, upto Rs. 500 per month',
    amount: '₹6000',
  },
  {
    icon: 'https://parkplus-bkt-img.parkplus.io/production/vehicle/public/fastag_20230828110228044758.png',
    title: ' worth of FASTag recharge',
    subtitle: '12 months of toll travel with Rs. 250 monthly FASTag recharges.',
    amount: '₹3000',
  },
  {
    icon: 'https://parkplus-bkt-img.parkplus.io/production/vehicle/public/shop_20230828110248750037.png',
    title: ' worth of car accessories',
    subtitle: 'An exclusively curated hamper of car accessories.',
    amount: '₹5000',
  },
];

export const TermsAndConditions = [
  '1. The car purchase invoice should be genuine.',
  '2. Only cars bought through the Park+ dealer fulfilling the test drive would be eligible for the offer.',
  '3. 10 Litres of petrol would be credited each month.',
];

export const POINTS = [
  '1. Take your test drive',
  '2. Upload your car purchase bill and get your 1st EMI Free',
];

export const initState = {
  name: {
    input: '',
    err: '',
  },
  email: {
    input: '',
    err: '',
  },
  phone: {
    input: '',
    err: '',
  },
  city: {
    input: '',
    err: '',
  },
  pincode: {
    input: '',
    err: '',
  },
  dealer_name: {
    input: '',
    err: '',
  },
};

export const OlaInitState = {
  name: {
    input: '',
    err: '',
  },
  phone: {
    input: '',
    err: '',
  },
  city: {
    input: '',
    err: '',
  },
  pincode: {
    input: '',
    err: '',
  },
};

// export const NEW_CARS_ROUTES = {
//   myBookings: '/services/test-drive/my-bookings',
// };

export enum ViewType {
  Vertical = 'vertical',
  Horizontal = 'horizontal',
}

export enum Actions {
  Yes = 'yes',
  No = 'no',
  Upload = 'upload',
}

export const CAR_IMAGE =
  'https://parkplus-bkt-img.parkplus.io/production/fuel-bill/public/Banner013_20230727191745795308.png';

export const Reasons = [
  {
    value: 'MISMATCH',
    label: 'Schedule/availability mismatches',
    showInput: false,
  },
  {
    value: 'NOT_INTERESTED',
    label: 'Not interested anymore',
    showInput: false,
  },
  {
    value: 'SCHEDULE_LATER',
    label: 'Scheduled for later',
    showInput: true,
  },
];

export enum LeadType {
  Oem = 'OEM',
  Dealer = 'DEALER',
}

export const ListingChips = ['Body type', 'Budget', 'Brand'];

export const PROMOTIONAL_BANNERS = [
  {
    redirectionUrl: '/money',
    image:
      'https://parkplus-bkt-img.parkplus.io/production/vehicle/public/car_20240917161707485463.png',
  },
];

export const MIN_REQUIRED_FIELD = {
  oem: 2,
  dealer: 1,
  oemWithAddress: 2,
  dealerWithAddress: 1,
};

export enum SocietyModels {
  Tiago = 'tiago',
  Comet = 'comet',
}

export enum PanoTabs {
  Exterior = 'EXTERIOR',
  Interior = 'INTERIOR',
}

export const TelePhonePayloadTypes = {
  sameModel: 'SAME_MODEL',
  bodyType: 'SIMILAR_BODY_TYPE',
  price: 'SIMILAR_PRICE',
};

export enum TestDriveVersion {
  v1 = 'v1',
  v2 = 'v2',
}

export enum CarReviewStep {
  Zero = 0,
  First = 1,
  Second = 2,
  Third = 3,
}

export enum LeadGenerationSource {
  Website = 'PARKPLUS_WEBSITE',
}

export const DEALER_TEXT = [
  'Find Park+ authorised dealerships near you',
  'Claim exclusive deals and offers',
  'Beat the queues, book instant test drives',
];

export const specsHeadToolTips = [
  {
    title: 'ADAS Feature',
    tooltip:
      'ADAS uses sensors and cameras to enhance driving safety by providing features like lane departure warnings and automatic braking',
  },
];

export const QuizQuestions = {
  questions: {
    budget: {},
    body_type: {},
    mileage: {},
    fuel_type: {},
  },
};

export const FuelType = [
  {
    key: 'diesel',
    label: 'Diesel',
  },
  {
    key: 'petrol',
    label: 'Petrol',
  },
  {
    key: 'cng',
    label: 'CNG',
  },
  {
    key: 'ev',
    label: 'EV (Electric Vehicle)',
  },
  // {
  //   key: 'hybrid',
  //   label: 'Hybrid',
  // },
];

export const QuizFilters = [
  {
    key: FilterWidgets.Budget,
    label: 'Budget',
  },
  {
    key: FilterWidgets.BodyType,
    label: 'Car body type',
  },
  {
    key: FilterWidgets.Mileage,
    label: 'Mileage',
  },
  {
    key: FilterWidgets.Fuel,
    label: 'Fuel type',
  },
  {
    key: FilterWidgets.Brands,
    label: 'Brands',
  },
  {
    key: FilterWidgets.SeatingCapacity,
    label: 'Seating capacity',
  },
  {
    key: FilterWidgets.FavFeatures,
    label: 'Favorite features',
  },
];

export enum SearchCardType {
  Filters = 'applied_filters',
  Search = 'search_card',
  ViewAll = 'view_All_widget',
  ExactCount = 'exact_count',
}

export const formatCarOwners = (users, reviewType = 'dealer_rating', title) => {
  const totalOwners = users.length;

  if (totalOwners === 0)
    return reviewType === 'dealer_rating'
      ? 'No one has bought a car from this dealer.'
      : 'No one drives this car.';

  if (totalOwners === 1)
    return reviewType === 'dealer_rating'
      ? `${users[0].userName} also bought a car from ${title}. Would you want to get their feedback on this dealer?`
      : `${users[0].userName} also drive a ${title}. Would you want to get their feedback on this car?`;

  if (totalOwners === 2)
    return reviewType === 'dealer_rating'
      ? `${users[0].userName} and ${users[1].userName} also bought a car from ${title}. Would you want to get their feedback on this dealer?`
      : `${users[0].userName} and ${users[1].userName} also drive a ${title}. Would you want to get their feedback on this car?`;

  const firstTwo = users
    .slice(0, 2)
    .map((user) => user.userName)
    .join(', ');

  return reviewType === 'dealer_rating'
    ? `${firstTwo}, and ${totalOwners - 2} other ${
        totalOwners - 2 > 1 ? 'friends' : 'friend'
      } also bought a car from ${title}. Would you want to get their feedback on this dealer?`
    : `${firstTwo}, and ${totalOwners - 2} other ${
        totalOwners - 2 > 1 ? 'friends' : 'friend'
      } also drive a ${title}. Would you want to get their feedback on this car?`;
};

export const generateServicedUsersLabel = (servicedUsers, length = 0) => {
  if (!servicedUsers.length) return '';
  if (servicedUsers.length === 1) {
    return `${servicedUsers[0]?.name} has bought car from this dealer`;
  }
  if (servicedUsers.length >= 2) {
    if (length > 0) {
      return `${servicedUsers[0]?.name}, ${
        servicedUsers[1]?.name
      }, and ${length} other friend${
        length > 1 ? 's' : ''
      } bought car from this dealer.`;
    }
    return `${servicedUsers[0]?.name} and ${servicedUsers[1]?.name} have bought car from this dealer`;
  }
};

export const generateAssistedLabel = (servicedUsers, length = 0) => {
  if (!servicedUsers.length) return '';

  if (servicedUsers.length === 1) {
    return `Your friend ${servicedUsers[0]} has been assisted by this salesperson!`;
  }

  if (servicedUsers.length === 2) {
    if (length > 0) {
      return `Your friends ${servicedUsers[0]}, ${servicedUsers[1]} and ${length} other friend${
        length > 1 ? 's' : ''
      } have been assisted by this salesperson!`;
    }
    return `Your friends ${servicedUsers[0]} and ${servicedUsers[1]} have been assisted by this salesperson!`;
  }

  return '';
};

export const generateReviewedUsersLabel = (servicedUsers) => {
  if (servicedUsers.length === 1) {
    return `${servicedUsers[0]?.name} has reviewed this dealer.`;
  } else if (servicedUsers.length === 2) {
    return `${servicedUsers[0]?.name} and ${servicedUsers[1]?.name} have reviewed this dealer.`;
  } else if (servicedUsers.length === 3) {
    return `${servicedUsers[0]?.name}, ${servicedUsers[1]?.name}, and 1 other friend have reviewed this dealer.`;
  } else if (servicedUsers.length > 3) {
    return `${servicedUsers[0]?.name}, ${servicedUsers[1]?.name}, and ${
      servicedUsers.length - 2
    } others have reviewed this dealer.`;
  }
  return '';
};

export enum SearchPageWidgetType {
  AppBar = 'search_app_bar',
  SearchWidget = 'search_widget',
  LatestLaunchWidget = 'latest_launch_widget',
  MostSearchedBrandWidget = 'most_search_brnad_widget',
  TrendingSearchWidget = 'trending_widget',
  DidYouKnow = 'did_you_know_widget',
  ExploreByWidget = 'explore_by_widget',
  RecentSearchesWidget = 'recent_searches_widget',
}

export const specificationToolTips = [
  {
    title: 'Seating Capacity',
    tooltip: 'The number of passengers the vehicle can seat',
  },
  {
    title: 'Petrol Mileage ARAI',
    tooltip: 'The distance a vehicle can travel per unit of fuel',
  },
  {
    title: 'Fuel Type',
    tooltip:
      'The type of fuel the vehicle uses, such as petrol, diesel, or electric',
  },
  {
    title: 'No of Airbags',
    tooltip: 'The number of airbags installed for passenger safety',
  },
  {
    title: 'Steering Type',
    tooltip:
      'The mechanism used to steer the vehicle, such as power steering or manual steering',
  },
  {
    title: 'Steering Column',
    tooltip:
      'The shaft that connects the steering wheel to the steering mechanism',
  },
  {
    title: 'Front Brake Type',
    tooltip:
      'The type of braking system used on the front wheels, such as disc brake',
  },
  {
    title: 'Rear Brake Type',
    tooltip:
      'The type of braking system used on the rear wheels, such as drum brakes',
  },
  {
    title: 'Length (mm)',
    tooltip: 'The total length of the vehicle from front to back',
  },
  {
    title: 'Width (mm)',
    tooltip: 'The overall width of the vehicle from side to side',
  },
  {
    title: 'Height (mm)',
    tooltip:
      'The total height of the vehicle from the ground to the highest point',
  },
  {
    title: 'Wheel Base (mm)',
    tooltip: 'The distance between the centers of the front and rear wheels',
  },
  {
    title: 'Front Tread (mm)',
    tooltip:
      'The distance between the front wheels measured across the width of the vehicle',
  },
  {
    title: 'Rear Tread (mm)',
    tooltip:
      'The distance between the rear wheels measured across the width of the vehicle',
  },
  {
    title: 'Kerb Weight (Kg)',
    tooltip:
      'The weight of the vehicle with all standard equipment, fluids, and a full tank of fuel, but without passengers or cargo',
  },
  {
    title: 'No of Doors',
    tooltip:
      'The total number of doors in the vehicle, including front, rear, and any other access doors',
  },
  {
    title: 'Emission Norm Compliance',
    tooltip: 'The environmental standards the vehicle’s emissions comply with',
  },

  {
    title: 'clutch type',
    tooltip:
      'The type of clutch mechanism used to engage and disengage the engine from the transmission',
  },
  {
    title: 'steering column',
    tooltip:
      'The shaft that connects the steering wheel to the steering mechanism',
  },
  {
    title: 'kerb weight',
    tooltip:
      'The weight of the vehicle with all standard equipment, fluids, and a full tank of fuel, but without passengers or cargo',
  },
  {
    title: 'gesture control for infotainment systems',
    tooltip:
      'Gesture Control allows operation of the infotainment system with simple hand movements for enhanced convenience and reduced distraction',
  },
  {
    title: 'engine type',
    tooltip: 'The specific design and configuration of the engine',
  },
  {
    title: 'displacement',
    tooltip:
      'The total volume of all the cylinders in an engine, usually measured in cc or litres',
  },
  {
    title: 'max power',
    tooltip:
      'The highest amount of power the engine can produce, often measured in horsepower (hp) or kilowatts (kW)',
  },
  {
    title: 'max torque',
    tooltip:
      'The maximum twisting force the engine can produce, measured in Newton-meters (Nm)',
  },
  {
    title: 'no. of cylinders',
    tooltip:
      'The number of cylinders in the engine where fuel combustion occurs',
  },
  {
    title: 'power',
    tooltip:
      "The vehicle's ability to perform work, generally referring to its engine power",
  },
  {
    title: 'seating capacity',
    tooltip: 'The number of passengers the vehicle can seat',
  },
  {
    title: 'mileage',
    tooltip: 'The distance a vehicle can travel per unit of fuel',
  },
  {
    title: 'fuel',
    tooltip:
      'The type of fuel the vehicle uses, such as petrol, diesel, or electric',
  },
  {
    title: 'no. of airbags',
    tooltip: 'The number of airbags installed for passenger safety',
  },
  {
    title: 'steering gear type',
    tooltip:
      'The type of gear mechanism that translates the rotational movement of the steering wheel into directional change of the wheels',
  },
  {
    title: 'front brake type',
    tooltip:
      'The type of braking system used on the front wheels, such as disc brake',
  },
  {
    title: 'rear brake type',
    tooltip:
      'The type of braking system used on the rear wheels, such as drum brakes',
  },
  {
    title: 'acceleration',
    tooltip: 'The rate at which the vehicle increases its speed',
  },
  {
    title: '0-100 kmph time',
    tooltip:
      'The time it takes for the vehicle to accelerate from 0 to 100 kilometers per hour',
  },
  {
    title: 'rear headroom',
    tooltip:
      "The vertical space available for passengers' heads in the rear seats",
  },
  {
    title: 'front headroom',
    tooltip:
      "The vertical space available for passengers' heads in the front seats",
  },
  {
    title: 'front shoulder room',
    tooltip:
      'The horizontal space available at shoulder height for passengers in the front seats',
  },
  {
    title: 'rear shoulder room',
    tooltip:
      'The horizontal space available at shoulder height for passengers in the rear seats',
  },
  {
    title: 'no. of doors',
    tooltip:
      'The total number of doors in the vehicle, including front, rear, and any other access doors',
  },
  {
    title: 'digital instrument clusters',
    tooltip:
      'Digital Instrument Clusters use high-resolution screens to display customizable driving information in real-time',
  },
  {
    title: 'panoramic sunroofs with anti-pinch',
    tooltip:
      'Panoramic sunroofs offer expansive views while anti-pinch technology prevents the sunroof from closing on obstacles',
  },
  {
    title: 'all wheel drive',
    tooltip:
      'AWD distributes power to all four wheels simultaneously, providing better traction and control on various surfaces',
  },
  {
    title: 'rear wheel drive',
    tooltip:
      'RWD sends power to the rear wheels, offering better handling and acceleration, particularly in sports cars',
  },
  {
    title: 'front wheel drive',
    tooltip:
      'FWD delivers power to the front wheels, providing good traction and efficiency in most driving conditions',
  },
];

export const carOptions = [
  { id: 'interested_in_different_car', label: 'Interested in different car' },
  { id: 'already_made_the_purchase', label: 'Already made the purchase' },
  { id: 'negative_reviews', label: 'Negative reviews' },
  { id: 'waiting_time_was_long', label: 'Waiting time was long' },
  { id: 'other', label: 'Other' },
];

export const dealerOptions = [
  {
    id: 'interested_in_different_dealer',
    label: 'Interested in different dealer',
  },
  { id: 'already_made_the_purchase', label: 'Already made the purchase' },
  { id: 'negative_reviews', label: 'Negative reviews' },
  {
    id: 'got_better_discount_at_other_dealer',
    label: 'Got better discount at other dealer',
  },
  { id: 'other', label: 'Other' },
];

export const LocalStorageKey = {
  userVisited: 'userVisited',
  recentCards: 'recentCards',
};

export const bgColorMapping = {
  'Hot Hatchbacks': `linear-gradient(136deg, ${semanticColors.bg.primary} 0%, ${utilityColors.border.rosewood_primary} 100%)`,
  'Elite Sedans': ` linear-gradient(135deg, ${semanticColors.bg.primary} 0%, ${utilityColors.border.blue_primary} 100%)`,
  'Comfort SUVs': ` linear-gradient(134deg, ${semanticColors.bg.primary} 0%, ${utilityColors.border.yellow_primary} 100%)`,
  'Futuristic SUVs': ` linear-gradient(134deg, ${semanticColors.bg.primary} 0%, ${utilityColors.border.orange_primary} 100%)`,
  'Family MPVs': `linear-gradient(134deg, ${semanticColors.bg.primary} 0%, ${utilityColors.border.purple_primary} 100%)`,
  'Cool Coupes': ` linear-gradient(134deg, ${semanticColors.bg.primary} 0%, ${utilityColors.border.emerald_primary}  100%)`,
};
